import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "check";

type SelectableCheck = { id: number; status: { status: string } };

/**
 * Which check is open on a Screening record view, mirrored into the URL.
 *
 * A React element held in state (the old checkContent) captures its props at
 * click time, so it can't be driven from a URL and doesn't notice a refetch.
 * This holds the id instead: on load, the URL's check wins if it still exists
 * on the record; otherwise the first check that isn't pending, or the first
 * check if every one is.
 */
export const useSelectedCheck = (allChecks?: SelectableCheck[] | null) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const fromUrl = searchParams.get(PARAM);
    return fromUrl ? Number(fromUrl) : null;
  });

  useEffect(() => {
    if (!allChecks || allChecks.length === 0) return;

    const fromUrl = searchParams.get(PARAM);
    const urlId = fromUrl ? Number(fromUrl) : null;

    if (urlId !== null && allChecks.some((check) => check.id === urlId)) {
      setSelectedId(urlId);
      return;
    }

    if (selectedId !== null && allChecks.some((check) => check.id === selectedId)) {
      return;
    }

    const firstActive = allChecks.find(
      (check) => check.status?.status !== "pending",
    );
    const defaultId = (firstActive ?? allChecks[0]).id;

    setSelectedId(defaultId);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(PARAM, String(defaultId));
        return next;
      },
      { replace: true },
    );
    // re-run only when the set of checks changes, not on every selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChecks]);

  const selectCheck = (id: number) => {
    setSelectedId(id);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set(PARAM, String(id));
      return next;
    });
  };

  return { selectedId, selectCheck };
};
